'use client';
import { FormEvent, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useTranslations } from 'next-intl';
import Head from 'next/head';
import { useRouter } from 'next/navigation';
import * as Yup from 'yup';

import {
  AuthDivider,
  AuthInput,
  AuthLayout,
  AuthPanel,
  AuthSubmitButton
} from '../../../components/Auth';
import GoogleOAuthButton from '../../../components/GoogleOAuthButton';
import { amplitudeInstance } from '../../../config/amplitude';
import env from '../../../config/env';
import constants from '../../../constants/constants.json';
import AuthContext from '../../../context/AuthContext';
import {
  AUTH_REDIRECT_FALLBACK_PATH,
  buildAuthPageLinkPath,
  sanitizeAuthRedirectTarget
} from '../../../helpers/authRedirect';
import { generateHrefLang } from '../../../helpers/generateHrefLang';
import useIntl from '../../../hooks/useIntl';
import { Link } from '../../../i18n/navigation';

interface FormValidationError {
  name: string;
  email: string;
  password: string;
}

interface PropTypes {
  redirectTarget?: string;
}

export default function SignUp({
  redirectTarget: rawRedirectTarget
}: PropTypes) {
  const intl = useIntl();
  const t = useTranslations();

  const auth = useContext(AuthContext);

  const router = useRouter();
  const redirectTarget = sanitizeAuthRedirectTarget(
    rawRedirectTarget,
    constants.I18.SUPPORTED_LOCALES
  );
  const shouldPreserveRedirectTarget =
    !!rawRedirectTarget && redirectTarget !== AUTH_REDIRECT_FALLBACK_PATH;
  const loginPath = buildAuthPageLinkPath(
    '/login',
    redirectTarget,
    shouldPreserveRedirectTarget
  );

  const [warning, setWarning] = useState<FormValidationError | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agree, setAgree] = useState(false);

  const [googleAccessToken, setGoogleAccessToken] = useState('');
  const [showAgree, setShowAgree] = useState(false);

  const googleLogin = useGoogleLogin({
    onSuccess: codeResponse => {
      setGoogleAccessToken(codeResponse.access_token);
      setShowAgree(true);
    },
    onError: () => showGoogleOAuthError(),
    onNonOAuthError: error => {
      if (error.type !== 'popup_closed') showGoogleOAuthError();
    },
    flow: 'implicit'
  });

  function showGoogleOAuthError() {
    toast(t('sign_up.errors.google_oauth_failed'), {
      position: 'bottom-center',
      type: 'error'
    });
  }

  useEffect(() => {
    if (!auth.isLoading && auth.isLoggedIn) {
      router.replace(redirectTarget);
    }
  }, [auth.isLoading, auth.isLoggedIn, redirectTarget, router]);

  useEffect(() => {
    amplitudeInstance.logEvent('Sign Up Page View');
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const schema = Yup.object().shape({
        name: Yup.string().max(60).required(t('sign_up.errors.required_name')),
        email: Yup.string()
          .email(t('sign_up.errors.invalid_email_format'))
          .required(t('sign_up.errors.required_email')),
        password: Yup.string().required(t('sign_up.errors.required_password'))
      });

      const isValid = await schema.validate(
        { name, email, password },
        { abortEarly: false }
      );

      if (isValid) {
        setWarning(null);
        if (!agree) {
          toast(
            t('sign_up.errors.you_need_to_agree_with_terms_and_privacy_policy'),
            { position: 'bottom-center', type: 'error' }
          );
          return;
        }

        await handleSignUp();
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        interface ValidationError extends FormValidationError {
          [key: string]: string;
        }

        const validationErrors: ValidationError = {
          name: '',
          email: '',
          password: ''
        };

        error.inner.forEach(validationError => {
          validationErrors[validationError.path as string] =
            validationError.message;
        });

        setWarning(validationErrors);
      }
    }
  }

  async function handleGoogleSignUp() {
    try {
      if (!agree) {
        toast(
          t('sign_up.errors.you_need_to_agree_with_terms_and_privacy_policy'),
          { position: 'bottom-center', type: 'error' }
        );
        return;
      }

      setGoogleLoading(true);
      const response = await axios.post('/api/oauth/', {
        code: googleAccessToken,
        type: 'sign_up',
        provider: 'google',
        client: 'web',
        selected_language: intl.full_language,
        agree
      });

      if (response.status === 200) {
        amplitudeInstance.setUserId(response.data.data._id);
        amplitudeInstance.logEvent('Google Sign Up');

        window.location.href = redirectTarget;
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.error?.code;

      if (status === 400 && code === 'EMAIL_ALREADY_USED') {
        toast(t('sign_up.errors.email_already_registred'), {
          position: 'bottom-center',
          type: 'error'
        });
        return;
      }

      if (status === 403) {
        toast(t('sign_up.errors.account_suspended'), {
          position: 'bottom-center',
          type: 'error'
        });
        return;
      }

      if (status === 429) {
        toast(t('sign_up.errors.rate_limit'), {
          position: 'bottom-center',
          type: 'error'
        });
        return;
      }

      showGoogleOAuthError();
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleSignUp() {
    try {
      setLoading(true);
      const response = await axios.post('/api/signup', {
        name,
        email,
        password,
        selected_language: intl.full_language,
        client: 'web',
        agree
      });

      setLoading(false);
      if (response.status === 200) {
        amplitudeInstance.setUserId(response.data.data._id);
        amplitudeInstance.logEvent('Sign Up');

        window.location.href = redirectTarget;
      }
    } catch (err: any) {
      setLoading(false);
      const status = err?.response?.status;
      const code = err?.response?.data?.error?.code;

      if (status === 400 && code === 'EMAIL_ALREADY_USED') {
        toast(t('sign_up.errors.email_already_registred'), {
          position: 'bottom-center',
          type: 'error'
        });
      }

      if (status === 429) {
        toast(t('sign_up.errors.rate_limit'), {
          position: 'bottom-center',
          type: 'error'
        });
      }

      if (status === 500) {
        toast(t('sign_up.errors.internal_server_error'), {
          position: 'bottom-center',
          type: 'error'
        });
      }
    }
  }

  const termsAgreement = (
    <label
      htmlFor={showAgree ? 'oauth-agree' : 'agree'}
      className="text-sm leading-6 text-gray-100"
    >
      {t('sign_up.agree_terms_of_use_and_privacy_policy.first')}{' '}
      <Link
        href="/terms-of-use"
        target="_blank"
        className="font-semibold text-white hover:text-red-600"
      >
        {t('common.terms-of-use')}
      </Link>{' '}
      {t('sign_up.agree_terms_of_use_and_privacy_policy.second')}{' '}
      <Link
        href="/privacy-policy"
        target="_blank"
        className="font-semibold text-white hover:text-red-600"
      >
        {t('common.privacy-policy')}
      </Link>
    </label>
  );

  return (
    <>
      <Head>
        <title>{`${t('sign_up.head_title')}`}</title>
        <meta property="og:title" content={`${t('sign_up.head_title')}`} />
        <meta
          key="description"
          name="description"
          content={t('sign_up.description')}
        />
        <meta
          key="og:description"
          name="og:description"
          content={t('sign_up.description')}
        />

        <meta
          key="og:image"
          name="og:image"
          content={'https://trakio.app/assets/og-preview.jpg'}
        />

        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${env.WEBAPP_URL}/signup`}
        />

        {Object.values(constants.CONTENT_REGIONS).map(locale => {
          return (
            <link
              key={locale}
              rel="alternate"
              hrefLang={generateHrefLang(locale)}
              href={`${env.WEBAPP_URL}/signup?region=${locale.toLowerCase()}`}
            />
          );
        })}
      </Head>

      <AuthLayout
        sideTitle={t('landing.title')}
        sideDescription={t('landing.subtitle')}
        highlights={[
          t('landing.get_the_app.title'),
          t('landing.find_where_to_watch.title'),
          t('landing.import_trakt_data.title')
        ]}
      >
        <AuthPanel
          title={t('sign_up.title')}
          description={
            showAgree
              ? t('sign_up.o_auth.agree_terms_of_use_and_privacy_policy_label')
              : t('sign_up.description')
          }
          footer={
            <>
              {t('sign_up.sign_in_prompt')}{' '}
              <Link
                href={loginPath}
                className="font-bold text-white hover:text-red-600"
              >
                {t('sign_up.sign_in_button')}
              </Link>
            </>
          }
        >
          {!showAgree && (
            <>
              <form className="flex flex-col" onSubmit={handleSubmit}>
                <AuthInput
                  id="name"
                  name="name"
                  type="text"
                  label={t('sign_up.name')}
                  placeholder={t('sign_up.name_placeholder')}
                  value={name}
                  autoComplete="name"
                  error={warning?.name}
                  onChange={e => setName(e.target.value)}
                />

                <AuthInput
                  id="email"
                  name="email"
                  type="email"
                  label={t('sign_up.email')}
                  placeholder={t('sign_up.email_placeholder')}
                  value={email}
                  autoComplete="email"
                  containerClassName="mt-5"
                  error={warning?.email}
                  onChange={e => setEmail(e.target.value)}
                />

                <AuthInput
                  id="password"
                  name="password"
                  type="password"
                  label={t('sign_up.password')}
                  placeholder={t('sign_up.password_placeholder')}
                  required
                  value={password}
                  autoComplete="new-password"
                  containerClassName="mt-5"
                  error={warning?.password}
                  onChange={e => setPassword(e.target.value)}
                />

                <div className="mt-5 flex items-start gap-3 rounded-md border border-white/10 bg-gray-600 p-3">
                  <input
                    id="agree"
                    checked={agree}
                    onChange={e => setAgree(e.target.checked)}
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-white/20 bg-black text-red-600 focus:ring-red-600"
                  />
                  {termsAgreement}
                </div>

                <div className="mt-6">
                  <AuthSubmitButton
                    label={t('sign_up.submit_button')}
                    loading={loading}
                  />
                </div>
              </form>

              <AuthDivider />

              <GoogleOAuthButton
                text={t('sign_up.sign_up_with_google')}
                onClick={() => googleLogin()}
                disabled={googleLoading}
              />
            </>
          )}

          {showAgree && (
            <div className="flex flex-col">
              <div className="flex items-start gap-3 rounded-md border border-white/10 bg-gray-600 p-3">
                <input
                  id="oauth-agree"
                  checked={agree}
                  onChange={e => setAgree(e.target.checked)}
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-white/20 bg-black text-red-600 focus:ring-red-600"
                />
                {termsAgreement}
              </div>

              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  className="flex h-12 items-center justify-center rounded-md border border-white/10 px-4 font-bold text-white transition hover:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-black"
                  type="button"
                  onClick={() => {
                    setGoogleAccessToken('');
                    setShowAgree(false);
                  }}
                  disabled={googleLoading}
                >
                  {t('sign_up.o_auth.go_back_button')}
                </button>

                <button
                  className="flex h-12 items-center justify-center rounded-md bg-red-600 px-4 font-bold text-white transition hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:ring-offset-2 focus:ring-offset-black"
                  type="button"
                  onClick={handleGoogleSignUp}
                  disabled={googleLoading}
                >
                  {t('sign_up.o_auth.continue_button')}
                </button>
              </div>
            </div>
          )}
        </AuthPanel>
      </AuthLayout>
    </>
  );
}
